import ApiService from "../../../services/ApiService";
import HomePuzzleCoinsService from "../../../services/HomePuzzleCoinsService";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";
import { getJigsawStoryTitle, pickJigsawSquareImageUrl } from "../../../pages/puzzles/jigsaw/jigsawBrowseUtils";

function mapPlayRow(row) {
    if (!row || typeof row !== "object") return null;

    const story = row.story ?? null;
    const puzzleTitle = getJigsawStoryTitle(story) || story?.id || "—";
    const isComplete = row.isCompleted === true;
    const jigsaw = row.jigsaw ?? null;

    return {
        id: row.id,
        name: `${row.cloudUser?.firstName ?? ""} ${row.cloudUser?.lastName ?? ""}`.trim() || "—",
        email: row.cloudUser?.email ?? "—",
        puzzleTitle,
        startedAt: UtilService.formatDate(row.startedAt),
        finishedAt: UtilService.formatDate(row.finishedAt),
        isCompleted: isComplete,
        status: isComplete ? "Completed" : "In Progress",
        attemptsCount: row.attemptsCount ?? 0,
        scatteredPieces: row.scatteredPieces ?? null,
        boardPositions: row.boardPositions ?? null,
        jigsawId: jigsaw?.id ?? null,
        imageUrl: pickJigsawSquareImageUrl(story),
        story,
        jigsaw,
        storyJigsaw: row.storyJigsaw ?? null,
        cloudUser: row.cloudUser ?? null,
    };
}

const AdminJigsawPlayService = {
    mapPlayRow,

    requestPuzzlePlay: async (jigsawId, keyword, pageModel, sort, setRows, setRowCount, isCompleted, onlyStarted = false, storyId) => {
        const params = {
            page: pageModel.page,
            pageSize: pageModel.pageSize,
            sort: sort.map((s) => `${s.field}:${s.sort}`).join(","),
        };

        if (jigsawId) {
            params.jigsawId = jigsawId;
        }

        if (storyId) {
            params.storyId = storyId;
        }

        if (keyword) {
            params.keyword = keyword;
        }

        if (isCompleted !== null) {
            params.isCompleted = isCompleted;
        }

        if (onlyStarted === true) {
            params.onlyStarted = true;
        }

        const result = await ApiService.getData("/v1/puzzle/jigsaw-play", params);

        const mapped = (result?.content ?? []).map(mapPlayRow).filter(Boolean);

        setRows(mapped);
        setRowCount(result?.page?.totalElements ?? result?.totalElements ?? 0);
    },

    listPlays: async ({ storyId, page = 0, pageSize = 8, sort = "startedAt:desc", isCompleted } = {}) => {
        const params = { page, pageSize, sort };
        if (storyId) {
            params.storyId = storyId;
        }
        if (isCompleted !== null && isCompleted !== undefined) {
            params.isCompleted = isCompleted;
        }
        const result = await ApiService.getData("/v1/puzzle/jigsaw-play", params);
        return {
            rows: (result?.content ?? []).map(mapPlayRow).filter(Boolean),
            total: result?.page?.totalElements ?? result?.totalElements ?? 0,
        };
    },

    requestPlayDetail: async (playId) => {
        if (!playId) {
            throw new Error("Play id is required");
        }

        try {
            const row = await ApiService.getData(`/v1/puzzle/jigsaw-play/${encodeURIComponent(playId)}`);
            const mapped = mapPlayRow(row);
            if (mapped) return mapped;
        } catch (error) {
            Logger.warn("GET /v1/puzzle/jigsaw-play/:id failed, falling back to list lookup", error);
        }

        const result = await ApiService.getData("/v1/puzzle/jigsaw-play", {
            page: 0,
            pageSize: 200,
            sort: "startedAt:desc",
        });
        const found = (result?.content ?? []).find((row) => row.id === playId);
        if (!found) {
            throw new Error("Play session not found");
        }
        return mapPlayRow(found);
    },

    forcePlacePieces: async (playId, count) => {
        const n = Number(count);
        if (!playId) {
            throw new Error("Play id is required");
        }
        if (!Number.isInteger(n) || n <= 0) {
            throw new Error("count must be a positive integer");
        }

        try {
            await ApiService.putData(
                `/v1/puzzle/jigsaw-play/${encodeURIComponent(playId)}/force-place-pieces/${n}`,
                {},
            );
            return true;
        } catch (error) {
            Logger.error("Error force-placing jigsaw pieces (admin):", error);
            throw error;
        }
    },

    deleteJigsawPlay: async (jigsawPlayIds) => {
        try {
            const ids = Array.isArray(jigsawPlayIds) ? jigsawPlayIds : [jigsawPlayIds];
            await ApiService.deleteData("/v1/puzzle/jigsaw-play", { jigsawPlayIds: ids });
            return true;
        } catch (error) {
            Logger.error("Error deleting jigsaw play:", error);
            throw error;
        }
    },

    /**
     * Metadata (no baskets) or cached baskets for a published jigsaw.
     * GET /home/jigsaw/{jigsawId}/color-baskets[?basketCount=K]
     */
    getColorBaskets: async (jigsawId, basketCount) => {
        if (!jigsawId) {
            throw new Error("Jigsaw id is required");
        }
        const params =
            basketCount != null && basketCount !== ""
                ? { basketCount: Number(basketCount) }
                : undefined;
        const response = await ApiService.getData(
            `/home/jigsaw/${encodeURIComponent(jigsawId)}/color-baskets`,
            params,
        );
        if (response?.code === "FAILURE") {
            throw new Error(response.message || "Failed to load color baskets");
        }
        return response;
    },

    /**
     * Spend puzzle coins to unlock one color basket (UC-02).
     * POST /home/jigsaw/{jigsawId}/color-baskets/unlock
     */
    unlockColorBaskets: async (jigsawId, basketCount, basketIndex) => {
        if (!jigsawId) {
            throw new Error("Jigsaw id is required");
        }
        const k = Number(basketCount);
        if (!Number.isInteger(k) || k < 2) {
            throw new Error("basketCount must be an integer >= 2");
        }
        const idx = Number(basketIndex);
        if (!Number.isInteger(idx) || idx < 0) {
            throw new Error("basketIndex must be an integer >= 0");
        }
        return HomePuzzleCoinsService.unlockColorBasket({
            jigsawId,
            basketCount: k,
            basketIndex: idx,
        });
    },

    /**
     * Admin force re-cluster for one (jigsawId, K).
     * POST /v1/puzzle/jigsaw/{jigsawId}/color-baskets/recompute?basketCount=K
     */
    recomputeColorBaskets: async (jigsawId, basketCount) => {
        if (!jigsawId) {
            throw new Error("Jigsaw id is required");
        }
        const k = Number(basketCount);
        if (!Number.isInteger(k) || k < 2) {
            throw new Error("basketCount must be an integer >= 2");
        }
        try {
            const response = await ApiService.postData(
                `/v1/puzzle/jigsaw/${encodeURIComponent(jigsawId)}/color-baskets/recompute?basketCount=${encodeURIComponent(k)}`,
                {},
            );
            if (response?.code === "FAILURE") {
                throw new Error(response.message || "Color basket recompute failed");
            }
            return response;
        } catch (error) {
            Logger.error("Error recomputing color baskets (admin):", error);
            throw error;
        }
    },
};

export default AdminJigsawPlayService;
